import { collection, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import userDataService from './userDataService';

/**
 * Fetch orders directly from Firestore with rich metadata and pagination support.
 * Implements repository pattern for data access with standardized response format.
 * 
 * @param {Object} options - Query options
 * @param {string} [options.storeId] - Store ID override (uses userDataService if not provided)
 * @param {string} [options.startDate] - YYYY-MM-DD inclusive start date
 * @param {string} [options.endDate] - YYYY-MM-DD inclusive end date
 * @param {string} [options.status] - Order status filter (pending, completed, etc.)
 * @param {string} [options.searchTerm] - Search term for order number or customer name
 * @param {number} [options.page=1] - Page number (1-indexed)
 * @param {number} [options.perPage=10] - Items per page
 * @param {string} [options.orderBy='createdAt'] - Field to sort by
 * @param {string} [options.orderDirection='desc'] - Sort direction ('asc' or 'desc')
 * @returns {Promise<Object>} - Standardized response with orders, pagination and metadata
 */
export const fetchOrdersFromFirestore = async (options = {}) => {
  // Resolver ID de tienda
  const storeId = options.storeId || userDataService.getStoreId();
  if (!storeId) {
    console.warn('[orderService.firestore] storeId no disponible');
    return { orders: [], totalPages: 0, totalItems: 0, page: 1, perPage: options.perPage || 10 };
  }

  try {
    // Configuración de consulta base
    const colRef = collection(db, 'stores', String(storeId), 'orders');
    const sortField = options.orderBy || 'createdAt';
    const sortDirection = options.orderDirection || 'desc';
    // Partes de la consulta. Se arma dinámicamente para cumplir las reglas de índices de Firestore.
    const qParts = [];
    
    // Configuración de paginación
    const page = parseInt(options.page) || 1;
    const perPage = parseInt(options.perPage) || 10;
    const maxDocs = perPage;

    // Filtro por status (string plano).
    // Aceptamos el valor tal cual venga del UI y generamos tres variantes para
    // tolerar posibles diferencias de mayúsculas/minúsculas en la base de datos.
    // Ej: "pending" => ["pending", "PENDING", "Pending"]
    const rawStatus = typeof options.status === 'string' && options.status !== 'all'
      ? String(options.status).trim()
      : null;
    const statusFilter = rawStatus ? rawStatus.toLowerCase() : null;
    if (statusFilter) {
      // Generamos lista de variantes a consultar
      // Sinónimos reconocidos (cancelled vs canceled, etc.)
      const synonymMap = {
        canceled: ['canceled', 'cancelled'],
        cancelled: ['cancelled', 'canceled'],
        "in_dispute": ['in_dispute', 'in-dispute', 'dispute']
      };
      const baseVariants = [statusFilter];
      if (synonymMap[statusFilter]) {
        baseVariants.push(...synonymMap[statusFilter]);
      }
      const variants = Array.from(new Set([
        ...baseVariants,
        ...baseVariants.map(v => v.toUpperCase()),
        ...baseVariants.map(v => v.charAt(0).toUpperCase() + v.slice(1))
      ]));
      // Utilizamos operador "in" para abarcar todas las variantes.
      // Asegúrate de contar con el índice compuesto correspondiente (status, createdAt).
      qParts.push(where('status', 'in', variants));
    }
    
    // Filtros de rango de fecha sobre createdAt
    if (options.startDate) {
      try {
        qParts.push(where(
        'createdAt',
        '>=',
        Timestamp.fromDate(new Date(`${options.startDate}T00:00:00Z`))
      ));
      } catch (e) {
        console.warn('[orderService.firestore] Error con startDate:', e);
      }
    }
    
    if (options.endDate) {
      try {
        qParts.push(where(
        'createdAt',
        '<=',
        Timestamp.fromDate(new Date(`${options.endDate}T23:59:59Z`))
      ));
      } catch (e) {
        console.warn('[orderService.firestore] Error con endDate:', e);
      }
    }

    // Ordenamiento principal por createdAt (requisito cuando hay filtros de rango).
    qParts.push(orderBy('createdAt', sortDirection));

    // Ordenamiento secundario opcional (si se solicitó y es distinto de createdAt)
    if (sortField && sortField !== 'createdAt') {
      qParts.push(orderBy(sortField, sortDirection));
    }
    // Log de depuración: mostrar partes de la consulta
    console.debug('[orderService.firestore] qParts', qParts.map(p => ({ type: p?.type || p?.constructor?.name || 'qc' })), {
      statusFilter,
      dateRange: { start: options.startDate, end: options.endDate },
      sort: { field: sortField, direction: sortDirection }
    });

    // Ejecutar consulta
    const q = query(colRef, ...qParts, limit(maxDocs));
    let snap = await getDocs(q);

    
    
    // Extraer documentos y metadatos
    let orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    

    
    // Obtener información de último sync
    let lastSync = null;
    try {
      const syncInfoRef = collection(db, 'stores', String(storeId), 'syncInfo');
      const syncSnap = await getDocs(query(syncInfoRef, orderBy('timestamp', 'desc'), limit(1)));
      if (!syncSnap.empty) {
        lastSync = syncSnap.docs[0].data();
      }
    } catch (e) {
      console.warn('[orderService.firestore] Error obteniendo syncInfo:', e);
    }
    
    // Calcular paginación (aproximada si no hay count exacto)
    // Esto es una aproximación ya que Firestore no tiene count() nativo
    // Recalcular totalItems después de filtrar
    const totalItems = orders.length < perPage ? orders.length : perPage * page + 1; // Estimación
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    
    console.log(`[orderService.firestore] storeId=${storeId} | docs=${orders.length} | page=${page}/${totalPages}`);
    
    // Retornar objeto con estructura estándar
    return {
      orders,
      page,
      perPage,
      totalPages,
      totalItems,
      lastSync,
      metadata: {
        source: 'firestore',
        storeId,
        queryOptions: { ...options }
      }
    };
  } catch (error) {
    console.error('[orderService.firestore] Error cargando órdenes:', error);
    // Retornar estructura estándar vacía en caso de error
    return {
      orders: [],
      page: 1,
      perPage: options.perPage || 10,
      totalPages: 0,
      totalItems: 0,
      error: error.message,
      metadata: {
        source: 'firestore',
        storeId,
        error: true
      }
    };
  }
};
